import { BrandOrderFulfillmentState } from '../../indigofair/data/BrandOrderFulfillmentState';
import { BrandOrderSource } from '../../indigofair/data/BrandOrderSource';
import { EstimatedShippingCostSource } from '../../indigofair/data/EstimatedShippingCostSource';
import { IAddress } from '../../indigofair/data/IAddress';
import { IBrandDiscount } from '../../indigofair/data/IBrandDiscount';
import { IBrandOrderCommissionReason } from '../../indigofair/data/IBrandOrderCommissionReason';
import { IBrandOrderIssueReport } from '../../indigofair/data/IBrandOrderIssueReport';
import { IBrandReview } from '../../indigofair/data/IBrandReview';
import { ICurrencyConversionRate } from '../../indigofair/data/ICurrencyConversionRate';
import { IFaireMoney } from '../../indigofair/data/IFaireMoney';
import { IFreeShippingInformation } from '../../indigofair/data/IFreeShippingInformation';
import { IListOrdersResponse } from '../../indigofair/data/IListOrdersResponse';
import { IMarkdownAllowance } from '../../indigofair/data/IMarkdownAllowance';
import { IMembership } from '../../indigofair/data/IMembership';
import { IOrder } from '../../indigofair/data/IOrder';
import { IOrderItem } from '../../indigofair/data/IOrderItem';
import { IOrderMechanismEnum } from '../../indigofair/data/IOrderMechanismEnum';
import { IPayment } from '../../indigofair/data/IPayment';
import { IPayoutTermEnum } from '../../indigofair/data/IPayoutTermEnum';
import { IShipment } from '../../indigofair/data/IShipment';
import { IShipmentItem } from '../../indigofair/data/IShipmentItem';
import { IShipWindow } from '../../indigofair/data/IShipWindow';
import { ITaxDetail } from '../../indigofair/data/ITaxDetail';
import { ITaxItem } from '../../indigofair/data/ITaxItem';
import { PaymentTerm } from '../../indigofair/data/PaymentTerm';
import { UserRole } from '../../indigofair/data/UserRole';

export namespace IBrandOrder {
  export enum State {
    /**
     * The brand order was created and is waiting to be fulfilled.
     */
    NEW = 'NEW',
    /**
     * The brand has been cancel (all order items were canceled).
     */
    CANCELED = 'CANCELED',
    /**
     * The brand has been contacted about this brand order but hasn't accepted it yet.
     */
    BRAND_CONTACTED = 'BRAND_CONTACTED',
    /**
     * The brand order is pending a review of the retailer.
     */
    WAITING_ON_REVIEW = 'WAITING_ON_REVIEW',
    /**
     * The brand marked some items as backordered or discontinued. We are waiting for the
     * Retailer to select how they want to proceed.
     */
    PENDING_RETAILER_CONFIRMATION = 'PENDING_RETAILER_CONFIRMATION',
    /**
     * The brand has accepted this brand order and is processing it but hasn't shipped it yet.
     */
    PROCESSING = 'PROCESSING',
    /**
     * This brand order has been shipped.
     */
    SHIPPED = 'SHIPPED',
    /**
     * This brand order has been delivered.
     */
    DELIVERED = 'DELIVERED',
    /**
     * All the items in this brand order are backordered.
     */
    BACKORDERED = 'BACKORDERED',
    /**
     * The brand order has a shipping cost adjustment request pending action by the retailer.
     */
    PENDING_RETAILER_SHIPPING_COST_ADJUSTMENT_CONFIRMATION = 'PENDING_RETAILER_SHIPPING_COST_ADJUSTMENT_CONFIRMATION',
    /**
     * The brand order is pending completed payment of overdue invoices by the retailer
     */
    WAITING_ON_RELEASE = 'WAITING_ON_RELEASE',
  }

  /**
   * This records "exceptions" on a brand order.
   * Exceptions may or may not cause a brand order to fail but we still want to track them.
   */
  export enum ExceptionReason {
    /**
     * Some of the items in this order are backordered.
     */
    BACKORDERED_ITEMS = 'BACKORDERED_ITEMS',
    /**
     * TODO: This had a typo. Delete after migrating. Use DISCONTINUED_ITEMS.
     * @deprecated
     */
    DISCOUNTINUED_ITEMS = 'DISCOUNTINUED_ITEMS',
    /**
     * Brand introduced a delay in fulfilling this order causing us to reach back to retailer.
     */
    DELAY = 'DELAY',
    /**
     * This brand order had items whose data was incorrect (e.g., incorrect case size).
     */
    INCORRECT_ITEM_DATA = 'INCORRECT_ITEM_DATA',
    /**
     * The brand had other exclusive relationships in the area of the retailer.
     */
    REJECTED_FOR_EXCLUSIVITY = 'REJECTED_FOR_EXCLUSIVITY',
    /**
     * The brand didn't like the retailer.
     */
    REJECTED_RETAILER_NO_MATCH = 'REJECTED_RETAILER_NO_MATCH',
    /**
     * The brand made a mistake and sent an incorrect amount and/or incorrect items.
     * This wasn't our fault but theirs.
     */
    INCORRECT_ITEMS_SHIPPED = 'INCORRECT_ITEMS_SHIPPED',
    /**
     * Some or all of the items arrived damaged.
     */
    DAMAGED_ITEMS = 'DAMAGED_ITEMS',
    /**
     * The retailer was deemed risky by us.
     */
    REJECTED_BY_INDIGO_FAIR = 'REJECTED_BY_INDIGO_FAIR',
    /**
     * This order contained items that were discontinued.
     */
    DISCONTINUED_ITEMS = 'DISCONTINUED_ITEMS',
    /**
     * The retailer claims they never received the package.
     */
    PACKAGE_NEVER_RECEIVED = 'PACKAGE_NEVER_RECEIVED',
    /**
     * There was a Faire-caused shipping error on this brand order. This order should be considered perfect.
     */
    FAIRE_CAUSED_SHIPPING_ERROR = 'FAIRE_CAUSED_SHIPPING_ERROR',
    /**
     * Used when manually overriding a specific brand order as perfect for the Top Shop program. The 'adminNote' field
     * should be used to capture the context of this override.
     */
    ADMIN_ADDED_IMPERFECT_ORDER_OVERRIDE = 'ADMIN_ADDED_IMPERFECT_ORDER_OVERRIDE',
  }

  /**
   * The reason why this brand order was created.
   */
  export enum CreationReason {
    NEW_ORDER = 'NEW_ORDER',
    BACKORDER = 'BACKORDER',
    REPLACE_DAMAGED_OR_MISSING_ITEMS = 'REPLACE_DAMAGED_OR_MISSING_ITEMS',
    SPLIT_ORDER = 'SPLIT_ORDER',
    ADMIN_CREATE_FAKE_REPLACEMENT_ORDER = 'ADMIN_CREATE_FAKE_REPLACEMENT_ORDER',
  }

  /**
   * The reason why this brand order was canceled.
   */
  export enum CancelReason {
    /**
     * Brand Reasons
     * Ensure that DbBrandOrder.BRAND_CANCEL_REASONS is kept in sync
     */
    REQUESTED_BY_RETAILER = 'REQUESTED_BY_RETAILER',
    RETAILER_NOT_GOOD_FIT = 'RETAILER_NOT_GOOD_FIT',
    ALL_ITEMS_CANCELED = 'ALL_ITEMS_CANCELED',
    BRAND_INITIATED_OTHER = 'BRAND_INITIATED_OTHER',
    SHOPIFY_OTHER = 'SHOPIFY_OTHER',
    ITEM_OUT_OF_STOCK = 'ITEM_OUT_OF_STOCK',
    INCORRECT_PRICING = 'INCORRECT_PRICING',
    ORDER_TOO_SMALL = 'ORDER_TOO_SMALL',
    /**
     * @deprecated
     */
    SHIPPING_COST_TOO_HIGH = 'SHIPPING_COST_TOO_HIGH',
    INSIDER_FREE_SHIPPING_PARTNERSHIP_PROGRAM_BCFS = 'INSIDER_FREE_SHIPPING_PARTNERSHIP_PROGRAM_BCFS',
    OTHER_NEARBY_CUSTOMER = 'OTHER_NEARBY_CUSTOMER',
    /**
     * Retailer Reasons
     * Ensure that DbBrandOrder.RETAILER_CANCEL_REASONS is kept in sync
     */
    PLACED_BY_MISTAKE = 'PLACED_BY_MISTAKE',
    CHANGE_REPLACE_ORDER = 'CHANGE_REPLACE_ORDER',
    LEAD_TIME_TOO_LONG = 'LEAD_TIME_TOO_LONG',
    PRODUCT_QUALITY_CONCERNS = 'PRODUCT_QUALITY_CONCERNS',
    ALTERNATIVES_ON_FAIRE = 'ALTERNATIVES_ON_FAIRE',
    CHEAPER_ELSEWHERE = 'CHEAPER_ELSEWHERE',
    NO_LONGER_NEEDED = 'NO_LONGER_NEEDED',
    ORDER_EDITED = 'ORDER_EDITED',
    /**
     * When picking other, it is expected that a note is also passed.
     */
    OTHER = 'OTHER',
    STUCK_WAITING_ON_REVIEW = 'STUCK_WAITING_ON_REVIEW',
    BRAND_INVOICE_PAYMENT_FAILED = 'BRAND_INVOICE_PAYMENT_FAILED',
    BRAND_INVOICE_DELETED = 'BRAND_INVOICE_DELETED',
    REJECT_INTERNATIONAL_ORDER = 'REJECT_INTERNATIONAL_ORDER',
    HOLD_PAYMENT_FAILED = 'HOLD_PAYMENT_FAILED',
    STUCK_PROCESSING = 'STUCK_PROCESSING',
    STUCK_BRAND_CONTACTED = 'STUCK_BRAND_CONTACTED',
    /**
     * Canceled due to being stuck in PENDING_RETAILER_CONFIRMATION for 6 months or longer
     */
    STUCK_PENDING_RETAILER_CONFIRMATION = 'STUCK_PENDING_RETAILER_CONFIRMATION',
    STUCK_BACKORDERED = 'STUCK_BACKORDERED',
    FORBIDDEN_ONLINE_ONLY = 'FORBIDDEN_ONLINE_ONLY',
    FORBIDDEN_SOCIAL_SELLER = 'FORBIDDEN_SOCIAL_SELLER',
    /**
     * All brand orders were rejected when the retailer was rejected
     */
    RETAILER_REJECTED_BY_ADMIN = 'RETAILER_REJECTED_BY_ADMIN',
    BRAND_BLACKLISTED = 'BRAND_BLACKLISTED',
    /**
     * If any other reasons used by the warehouse are added,
     * they should also be added to InventoryManger.WAREHOUSE_CANCELLATION_REASONS
     */
    WAREHOUSE_LOST = 'WAREHOUSE_LOST',
    WAREHOUSE_DAMAGED = 'WAREHOUSE_DAMAGED',
    WAREHOUSE_WRONG_QUANTITY = 'WAREHOUSE_WRONG_QUANTITY',
    WAREHOUSE_FTC_VIOLATION = 'WAREHOUSE_FTC_VIOLATION',
    ALL_ITEMS_BACKORDERED = 'ALL_ITEMS_BACKORDERED',
    SHIPPING_COST_ADJUSTMENT_REJECTED = 'SHIPPING_COST_ADJUSTMENT_REJECTED',
    STUCK_WAITING_ON_RELEASE = 'STUCK_WAITING_ON_RELEASE',
    OVERDUE_INVOICE_PAYMENT_FAILED = 'OVERDUE_INVOICE_PAYMENT_FAILED',
    /**
     * Automatically cancelled by DS-driven ETL
     */
    AUTO_CANCEL_BY_ETL = 'AUTO_CANCEL_BY_ETL',
  }

  export enum FeeStrategy {
    /**
     * calculate commission and payout fee on the pre discounted amount of a brand order
     * @deprecated
     */
    PRE_BRAND_FUNDED_DISCOUNT = 'PRE_BRAND_FUNDED_DISCOUNT',
    /**
     * calculate commission and payout fee on the post discounted amount of a brand order
     */
    POST_BRAND_FUNDED_DISCOUNT = 'POST_BRAND_FUNDED_DISCOUNT',
  }

  export namespace ITag {
    export enum Type {
      /**
       * The order was attributed to a Direct Marketing campaign the brand sent.
       * @deprecated
       */
      DIRECT_MARKETING_CAMPAIGN = 'DIRECT_MARKETING_CAMPAIGN',
      /**
       * The order was attributed to something else (e.g. a sales rep the brand is working with).
       */
      ATTRIBUTION = 'ATTRIBUTION',
      /**
       * The order was attributed to a Faire Affiliate.
       */
      AFFILIATE_SOURCED = 'AFFILIATE_SOURCED',
      /**
       * The order was placed while the user was in an Affiliate Linked Session AND at least ONE of the
       * items is from that Affiliate's Boards. Since Boards are editable it's important to tag here.
       */
      AFFILIATE_COLLECTION_PURCHASE = 'AFFILIATE_COLLECTION_PURCHASE',
      /**
       * A payment of this order has been attempted and has failed after the due date.
       */
      HAS_FAILED_OVERDUE_PAYMENT = 'HAS_FAILED_OVERDUE_PAYMENT',
      /**
       * This brand order skipped the verification process because of a Faire Direct relationship
       */
      PASSED_THROUGH_VERIFICATION_DUE_TO_RELATIONSHIP = 'PASSED_THROUGH_VERIFICATION_DUE_TO_RELATIONSHIP',
      /**
       * When this order was cancelled, it originated from an unverified Faire Direct Retailer
       */
      CANCELLED_BRAND_ORDER_ORIGINATED_FROM_UNVERIFIED_FAIRE_DIRECT_RETAILER = 'CANCELLED_BRAND_ORDER_ORIGINATED_FROM_UNVERIFIED_FAIRE_DIRECT_RETAILER',
      /**
       * One of the payments was disputed
       */
      DISPUTED = 'DISPUTED',
      /**
       * The order was attributed to a sample the brand sent to the same retailer prior to the order being placed.
       */
      SAMPLE_REQUEST = 'SAMPLE_REQUEST',
      /**
       * The order has had its due date extended by Admin
       */
      HAD_DUE_DATE_MANUALLY_EXTENDED = 'HAD_DUE_DATE_MANUALLY_EXTENDED',
      /**
       * The order was attributed to a marketing campaign the brand sent when the recipient opens the email.
       */
      MARKETING_CAMPAIGN_OPENED = 'MARKETING_CAMPAIGN_OPENED',
      /**
       * The order was attributed to a marketing campaign the brand sent when the recipient clicks the email.
       */
      MARKETING_CAMPAIGN_CLICKED = 'MARKETING_CAMPAIGN_CLICKED',
      /**
       * The brand order has a billing invoice associated with it
       */
      HAS_BILLING_INVOICE = 'HAS_BILLING_INVOICE',
    }

    /**
     * Return an instance of ITag with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ITag>): ITag =>
      ({
        ...(partial ?? {}),
      }) as ITag;
  }

  /**
   * A way to tag a brand order with a key/value pair, optionally specifying a URL to link to.
   */
  export interface ITag {
    name: string | undefined;
    value: string | undefined;
    url: string | undefined;
    type: keyof typeof IBrandOrder.ITag.Type | undefined;
    data: string | undefined;
  }

  export namespace IConversionRate {
    /**
     * Describes the event related to this brand order when the rate was captured.
     */
    export enum Event {
      /**
       * brand currency to retailer currency
       */
      CREATED = 'CREATED',
      SHIPPED = 'SHIPPED',
      PAID = 'PAID',
      /**
       * TODO(ryan.orr): Clean up currency conversion models
       *  We needed to make a separate event for brands to prevent breaking DS dashboards.
       */
      CREATED_FOR_BRAND = 'CREATED_FOR_BRAND',
      /**
       * brand currency to USD for finance and data science.
       */
      CREATED_BRAND_TO_FAIRE = 'CREATED_BRAND_TO_FAIRE',
      /**
       * retailer currency to USD for finance and data science.
       */
      CREATED_RETAILER_TO_FAIRE = 'CREATED_RETAILER_TO_FAIRE',
    }

    /**
     * Return an instance of IConversionRate with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IConversionRate>
    ): IConversionRate =>
      ({
        ...(partial ?? {}),
      }) as IConversionRate;
  }

  export interface IConversionRate {
    event: keyof typeof IBrandOrder.IConversionRate.Event | undefined;
    captured_at: number | undefined;
    rate: ICurrencyConversionRate | undefined;
  }

  export enum FreeDutiesReason {
    FREE_DUTIES_REASON_UNKNOWN = 'FREE_DUTIES_REASON_UNKNOWN',
    FREE_DUTIES_PROMO_2021 = 'FREE_DUTIES_PROMO_2021',
    EUROPEAN_BRAND_FREE_DUTIES_PROMO_2021 = 'EUROPEAN_BRAND_FREE_DUTIES_PROMO_2021',
    INSIDER_FREE_DUTIES = 'INSIDER_FREE_DUTIES',
  }

  export enum MarkAsDeliveredReason {
    MARK_AS_DELIVERED_REASON_UNKNOWN = 'MARK_AS_DELIVERED_REASON_UNKNOWN',
    RETAILER_MARKED_DELIVERED = 'RETAILER_MARKED_DELIVERED',
    ADMIN_FULFILL_REPLACEMENT_ORDER = 'ADMIN_FULFILL_REPLACEMENT_ORDER',
  }

  export enum BrandActionDeadlineType {
    BRAND_ACTION_DEADLINE_TYPE_UNKNOWN = 'BRAND_ACTION_DEADLINE_TYPE_UNKNOWN',
    /**
     * The ActionDeadlineAt represents the deadline for the brand to accept the order
     */
    ORDER_ACCEPT_DEADLINE = 'ORDER_ACCEPT_DEADLINE',
    /**
     * The ActionDeadlineAt represents the deadline for the brand to ship the order
     */
    SHIP_DEADLINE = 'SHIP_DEADLINE',
    /**
     * TODO(chinmayanathany): Delete this after the backfill has been run
     */
    ACTION_DEADLINE_TYPE_UNKNOWN = 'ACTION_DEADLINE_TYPE_UNKNOWN',
    /**
     * The ActionDeadlineAt represents the deadline for the brand to enter the estimated ship date for backorders
     */
    BACKORDER_ENTER_ESTIMATED_SHIP_DATE_DEADLINE = 'BACKORDER_ENTER_ESTIMATED_SHIP_DATE_DEADLINE',
  }

  /**
   * Redacted copy of IBrandOrder (i.e. without redacted fields).
   */
  export type Redacted = Omit<
    IBrandOrder,
    | 'admin_notes'
    | 'outgoing_upfront_payment'
    | 'late_fee_applied_at'
    | 'canceling_user_email'
    | 'created_at'
    | 'updated_at'
    | 'spent_from_balance'
    | 'taxable_order_invoice_number'
    | 'taxable_commission_invoice_number'
  >;

  /**
   * Return an instance of IBrandOrder with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IBrandOrder>): IBrandOrder =>
    ({
      shipments: [],
      pending_shipments: [],
      order_items: [],
      applied_discounts: [],
      issue_reports: [],
      tags: [],
      conversion_rates: [],
      duties_and_taxes_estimates: [],
      chargeable_tax_items: [],
      shipment_items: [],
      markdown_allowances: [],
      ...(partial ?? {}),
    }) as IBrandOrder;
}

/**
 * This represents an order placed with a Brand in order to fulfil a Retailer order.
 */
export interface IBrandOrder {
  token: string | undefined;
  /**
   * If this brand order has a parent, for example due to a back order, this will be set.
   */
  original_brand_order_token: string | undefined;
  state: keyof typeof IBrandOrder.State | undefined;
  fulfillment_state: keyof typeof BrandOrderFulfillmentState | undefined;
  /**
   * The creation reason for this brand order.
   */
  creation_reason: keyof typeof IBrandOrder.CreationReason | undefined;
  /**
   * The token of the employee user who is responsible for this brand order.
   */
  owner_token: string | undefined;
  /**
   * The mechanism used to send this brand order to the maker.
   */
  mechanism: keyof typeof IOrderMechanismEnum.OrderMechanism | undefined;
  /**
   * The payment term to be used for this brand order.
   */
  payment_term: keyof typeof PaymentTerm | undefined;
  /**
   * The payment term fee in bps to be applied to this brand order.
   * @deprecated
   */
  payment_term_fee_bps: number | undefined;
  /**
   * The payout term used to decide when to pay the brand for this brand order.
   */
  payout_term: keyof typeof IPayoutTermEnum.PayoutTerm | undefined;
  /**
   * The commission charged for this brand order in bps.
   */
  payout_commission_bps: number | undefined;
  /**
   * The percentage commission portion amount charged for this brand order
   */
  payout_commission: IFaireMoney | undefined;
  /**
   * @deprecated
   */
  payout_commission_cents: number | undefined;
  commission_reason:
    | keyof typeof IBrandOrderCommissionReason.Reason
    | undefined;
  fee_strategy: keyof typeof IBrandOrder.FeeStrategy | undefined;
  /**
   * The payout fee charged for this brand order in bps.
   */
  payout_fee_bps: number | undefined;
  /**
   * The flat fee portion of payout fee charged for this brand order
   */
  payout_fee_base: IFaireMoney | undefined;
  /**
   * The total payout fee charged for this brand order in cents.
   */
  payout_fee: IFaireMoney | undefined;
  /**
   * @deprecated
   */
  payout_fee_cents: number | undefined;
  /**
   * The gross payout total amount in cents for this brand order before the fee and commission.
   */
  payout_gross_total: IFaireMoney | undefined;
  /**
   * This is discounted by any item-level discounts if present, i.e. [payoutGrossTotal] - item_level_discount_total
   */
  item_level_discounted_payout_gross_total: IFaireMoney | undefined;
  /**
   * @deprecated
   */
  payout_gross_total_cents: number | undefined;
  /**
   * The net payout total amount in cents for this brand order after the fee and commission.
   */
  payout_total: IFaireMoney | undefined;
  /**
   * When we should initiate a payment to the brand for this brand order.
   */
  payout_due_at: number | undefined;
  /**
   * Cost of payout protection that was paid (if used) or price to pay (if not yet used).
   */
  payout_protection_fee: IFaireMoney | undefined;
  /**
   * Whether payout protection was bought. *
   */
  has_payout_protection: boolean | undefined;
  /**
   * The fixed retailer shipping cost for this brand order.
   */
  fixed_retailer_shipping_cost: IFaireMoney | undefined;
  /**
   * The fixed brand shipping cost for this brand order.
   */
  fixed_brand_shipping_cost: IFaireMoney | undefined;
  /**
   * The fixed brand shipping cost adjustment for this brand order.
   * DEPRECATED: Adjustments now override the fixedBrandShippingCost field, for the original value use
   * DbShippingCostAdjustment.originalAmount
   * @deprecated
   */
  fixed_brand_shipping_cost_adjustment: IFaireMoney | undefined;
  /**
   * A estimated shipment cost in cents set for this brand order.
   */
  estimated_shipping_cost: IFaireMoney | undefined;
  estimated_shipping_cost_source:
    | keyof typeof EstimatedShippingCostSource
    | undefined;
  /**
   * Whether we should automatically charge for this Brand order.
   */
  auto_charge: boolean | undefined;
  /**
   * When this brand order is due based on its payment term.
   * This is set to the shipment date (after order is shipped) for PAYMENT_ON_SHIPMENT terms.
   */
  due_at: number | undefined;
  /**
   * This token or ID given by the Brand to this brand order.
   */
  external_token: string | undefined;
  /**
   * This is the token of the Order order associated with this brand order.
   */
  order_token: string | undefined;
  /**
   * The Brand token for this brand order.
   */
  brand_token: string | undefined;
  /**
   * This commission charged on this brand order, in base points.
   * DEPRECATED: Use payoutCommissionBps instead!
   * @deprecated
   */
  commission_bps: number | undefined;
  /**
   * Additional notes to send to the Brand with this brand order.
   */
  notes: string | undefined;
  /**
   * Additional notes to send to the Brand from the retailer.
   */
  retailer_notes: string | undefined;
  /**
   * Additional notes to send to the Retailer from the brand.
   */
  brand_notes: string | undefined;
  /**
   * Additional notes recorded by admin.
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  admin_notes: string | undefined; // redacted
  /**
   * DEPRECATED: Use outgoingPayment instead!
   * @deprecated
   */
  payment: IPayment | undefined;
  /**
   * The payment from the Retailer to Faire for this brand order.
   */
  incoming_payment: IPayment | undefined;
  /**
   * The payment from Faire to the Brand for this brand order.
   */
  outgoing_payment: IPayment | undefined;
  /**
   * The upfront payment from Faire to the brand for this brand order
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  outgoing_upfront_payment: IPayment | undefined; // redacted
  /**
   * The shipment for this brand order. Use shipments instead.
   * @deprecated
   */
  shipment: IShipment | undefined;
  /**
   * The shipments for this brand order
   */
  shipments: Array<IShipment>;
  /**
   * Shipments that will be added to this brand order at a later point in time.
   * DEPRECATED: this field has always been empty.
   */
  pending_shipments: Array<IShipment>;
  /**
   * The order items for this brand order.
   */
  order_items: Array<IOrderItem>;
  /**
   * When the maker for this brand order was first contacted.
   */
  brand_contacted_at: number | undefined;
  /**
   * When the Maker requested confirmation for backordered items at.
   */
  pending_retailer_confirmation_at: number | undefined;
  /**
   * When the Retailer selected how to proceed with backordered items.
   */
  retailer_confirmed_at: number | undefined;
  /**
   * When the maker for this brand order accepted the order and started processing it.
   */
  processing_at: number | undefined;
  /**
   * Use expectedShipWindow instead.
   * @deprecated
   */
  expected_ship_date: number | undefined;
  expected_ship_window: IShipWindow | undefined;
  /**
   * When this brand order shipped.
   */
  shipped_at: number | undefined;
  /**
   * When this brand order was delivered.
   */
  delivered_at: number | undefined;
  /**
   * When this brand order was marked as backordered.
   */
  backordered_at: number | undefined;
  /**
   * If this brand order was created via a split from another brand order, this is the timestamp when
   * that split happened. Similar to [backorderedAt] but for [creationReason] = `SPLIT_ORDER`.
   */
  split_at: number | undefined;
  /**
   * When we paid the brand for this brand order.
   */
  paid_at: number | undefined;
  /**
   * How many reminders we have sent to the brand requesting to accept this brand order.
   */
  acceptance_reminders_sent: number | undefined;
  /**
   * The timestamp of the last acceptance reminder sent, if any.
   */
  last_acceptance_reminder_sent_at: number | undefined;
  /**
   * How many reminders we have sent to the brand requesting to fulfill this brand order.
   */
  fulfillment_reminders_sent: number | undefined;
  /**
   * The timestamp of the last fulfillment reminder sent, if any.
   */
  last_fulfillment_reminder_sent_at: number | undefined;
  /**
   * The timestamp when this brand order was moved into review state.
   */
  waiting_on_review_at: number | undefined;
  /**
   * The timestamp when this brand order was moved into waiting on release state.
   */
  waiting_on_release_at: number | undefined;
  /**
   * Record problems with this brand order.
   */
  exception_reason: keyof typeof IBrandOrder.ExceptionReason | undefined;
  /**
   * Whether this is the first time that the ordering retailer has ordered this brand (excluding flash sale orders).
   * It will be true for any orders with that brand in that day
   * For example, if a retailer ordered twice with this brand in the same day, both orders will
   * have firstOrderForBrand = true but only the first one will have veryFirstOrderForBrand = true
   */
  first_order_for_brand: boolean | undefined;
  /**
   * Whether this is the first time that the ordering retailer has ordered this brand (excluding flash sale orders).
   */
  very_first_order_for_brand: boolean | undefined;
  /**
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  late_fee_applied_at: number | undefined; // redacted
  /**
   * When the brand order failed to sync to the brand's POS system, or null if it didn't fail.
   */
  pos_sync_failed_at: number | undefined;
  /**
   * If set this brand order was created from a flash sale.
   */
  flash_sale_token: string | undefined;
  /**
   * The type of the retailer's active membership when the brand order was created.
   */
  retailer_membership_type: keyof typeof IMembership.Type | undefined;
  /**
   * If the brand had an active Elevate membership, and the retailer had a relationship with them.
   */
  is_elevate_order: boolean | undefined;
  cancel_reason: keyof typeof IBrandOrder.CancelReason | undefined;
  cancel_note: string | undefined;
  canceled_by_brand: boolean | undefined;
  canceled_at: number | undefined;
  /**
   * If the brand order was created by paying a brand invoice.
   */
  is_invoice_order: boolean | undefined;
  brand_invoice_created_at: number | undefined;
  /**
   * True if the brand order was created by paying a trade show invoice.
   */
  is_trade_show_order: boolean | undefined;
  /**
   * TODO(Dan): Delete this field once the front end stops using it
   */
  maker_discount_bps: number | undefined;
  /**
   * This should be set iff the retailer scheduled the order to ship at a specific date in the future vs shipping ASAP
   * It should not be changed after order placement
   */
  original_scheduled_order_date: number | undefined;
  /**
   * This should be set iff the order contains preorderable products
   * This is the earliest date the preorderable products in this order were scheduled to ship
   * It is set from Product#expectedShipDate and Product#expectedShipDateWindowDays and
   * should not be changed after order placement
   */
  original_preorder_ship_window: IShipWindow | undefined;
  return_window_extension_fee: IFaireMoney | undefined;
  applied_discounts: Array<IBrandDiscount>;
  retailer_requested_cancellation_at: number | undefined;
  cancellation_rejected_at: number | undefined;
  cancellation_response_note: string | undefined;
  canceling_user_role: keyof typeof UserRole | undefined;
  /**
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  canceling_user_email: string | undefined; // redacted
  /**
   * Review associated with this brand order *
   */
  brand_review: IBrandReview | undefined;
  /**
   * True if we should prompt the retailer for a brand review, only populated on orders page *
   */
  should_ask_for_brand_review: boolean | undefined;
  source: keyof typeof BrandOrderSource | undefined;
  /**
   * True if shipping is free.
   */
  free_shipping: boolean | undefined;
  previous_due_at: number | undefined;
  returnable_until: number | undefined;
  issue_reports: Array<IBrandOrderIssueReport>;
  version: number | undefined;
  /**
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  created_at: number | undefined; // redacted
  /**
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  updated_at: number | undefined; // redacted
  /**
   * This is just a sum of dutiesAndTaxesEstimates.amountCents.
   */
  estimated_duties_and_taxes: IFaireMoney | undefined;
  /**
   * @deprecated
   */
  estimated_duties_and_taxes_cents: number | undefined;
  /**
   * @deprecated
   */
  actual_duties_and_taxes_cents: number | undefined;
  retailer_chargeable_taxes_total: IFaireMoney | undefined;
  brand_payout_taxes_total: IFaireMoney | undefined;
  issue_reportable_until: number | undefined;
  tags: Array<IBrandOrder.ITag>;
  /**
   * The fixed commission amount charged for this brand order, in addition to commission in bps.
   */
  payout_commission_base: IFaireMoney | undefined;
  /**
   * @deprecated
   */
  payout_commission_base_cents: number | undefined;
  /**
   * The total commission amount (payoutCommission + payoutCommissionBase).
   */
  total_commission_amount: IFaireMoney | undefined;
  conversion_rates: Array<IBrandOrder.IConversionRate>;
  /**
   * Estimates of duties and taxes at the time the brand order was created.
   * THIS NOT CURRENTLY UPDATED FOR RETURNS or other modifications to the brand order.
   */
  duties_and_taxes_estimates: Array<ITaxDetail>;
  retailer_token: string | undefined;
  report_issue_eligibility:
    | keyof typeof IListOrdersResponse.IBrandOrderData.Eligibility
    | undefined;
  ignore_max_days_to_report_issues: boolean | undefined;
  /**
   * Use markdownAllowances instead
   * @deprecated
   */
  markdown_allowance: IMarkdownAllowance | undefined;
  ship_from_address: IAddress | undefined;
  shipping_address: IAddress | undefined;
  retailer_business_address: IAddress | undefined;
  brand_business_address: IAddress | undefined;
  /**
   * Taxes that will be charged to the retailer and paid out to the brand. ex: intra-canada tax
   */
  chargeable_tax_items: Array<ITaxItem>;
  shipping_cost: IFaireMoney | undefined;
  /**
   * If retailer and brand have R2M relationship
   */
  is_r2m_order: boolean | undefined;
  platform: keyof typeof IOrder.Platform | undefined;
  brand_shipping_subsidy: IFaireMoney | undefined;
  brand_shipping_subsidy_bps: number | undefined;
  /**
   * The total amount of the brand order covered by the brand through brand promos, including free shipping promos.
   */
  brand_funded_discounts_and_shipping: IFaireMoney | undefined;
  free_shipping_reason:
    | keyof typeof IFreeShippingInformation.FreeShippingReason
    | undefined;
  is_sample_order: boolean | undefined;
  shipment_items: Array<IShipmentItem>;
  is_purchase_order: boolean | undefined;
  purchase_order_token: string | undefined;
  free_duties_reason: keyof typeof IBrandOrder.FreeDutiesReason | undefined;
  duties_reported_by_retailer: IFaireMoney | undefined;
  /**
   * The amount of Faire credit that has been moved from the retailer's balance to the brand order, but not spent.
   */
  available_balance: IFaireMoney | undefined;
  /**
   * The amount of Faire credit that has been spent on the brand order.
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  spent_from_balance: IFaireMoney | undefined; // redacted
  /**
   * Taxable order invoice number for the invoice issued between brand and retailer.
   * Each brand has an invoice number that increments with each order
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  taxable_order_invoice_number: number | undefined; // redacted
  /**
   * Taxable commission invoice number for the invoice issued between Faire and the brand.
   * The invoice number increments globally for each relevant order
   * [REDACTED]
   * @protobufOption (indigofair.data.redacted) = true
   */
  taxable_commission_invoice_number: number | undefined; // redacted
  mark_as_delivered_reason:
    | keyof typeof IBrandOrder.MarkAsDeliveredReason
    | undefined;
  has_non_returnable_partner_brand: boolean | undefined;
  /**
   * used during hackweek, no longer used
   * @deprecated
   */
  carbon_offset_fee: IFaireMoney | undefined;
  brand_currency: string | undefined;
  /**
   * A short note-to-self which gets printed on the packing slip for the brand order.
   */
  retailer_packing_slip_notes: string | undefined;
  delivered_at_formatted: string | undefined;
  shipped_at_formatted: string | undefined;
  markdown_allowances: Array<IMarkdownAllowance>;
  original_estimated_delivery_date_lower_bound: number | undefined;
  original_estimated_delivery_date_upper_bound: number | undefined;
  brand_action_deadline_type:
    | keyof typeof IBrandOrder.BrandActionDeadlineType
    | undefined;
  brand_action_deadline_at: number | undefined;
  retailer_currency: string | undefined;
  is_flash_sale_order: boolean | undefined;
  /**
   * The amount of the total free shipping costs covered by the brand due to being an Insider partner.
   * Will only be filled for the payout commission and payout fee calculations.
   */
  insider_partner_shipping_cost_amount: IFaireMoney | undefined;
  /**
   * How much both the brand and Faire spent on shipping combined (negated). Will only be filled for the
   * payout calculation.
   * SwF: packaging/handling cost reported by brand (brandPaidAmount) + the SwF label paid by Faire
   * SoYo: the brand self reported shipping cost (brandPaidAmount), shipWithFaireAmount is $0
   */
  total_combined_shipping_cost: IFaireMoney | undefined;
}
